import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import {
  fetchMultiTenantEntityDetail,
  filterEntityPinsForKnockStatusLocationsOnly,
  filterLocationsByEntityTypes,
  filterLocationsByKnockStatus,
  getContactLocationsInArea,
  getEntityDetail,
  getEntityProducts,
} from './api'
import { areColocatedCoordinates } from './colocation'
import { mergePestPacPinsForward } from './pestPacPin'
import {
  EntityDetail,
  EntityLocationDTO,
  MultiTenantLocation,
  MultiTenantLocationDetails,
  SalespersonFilterOption,
  SearchAreaBoundary,
  SearchAreaResponse,
  PestPacLocationDetailResponseDTO,
} from './typings'
import type { BasicLocationDTO, LocationDTO } from '@sales-center/common'
import { matchEntityByAddress } from '@user/utils/addressMatchingUtils'
import { MapPinProps } from '@user/components/HereMap/MapPin/types'
import { DraftLead } from '../draftLeads'

interface MapState {
  entityDetails: EntityDetail | null
  selectedServiceLocationId?: number
  selectedServiceLocations?: MultiTenantLocation[]
  selectedServiceLocationsDetails?: MultiTenantLocationDetails[]
  selectedServiceLocationsDetailsStatus?:
    | 'empty'
    | 'loading'
    | 'error'
    | 'success'
  selectedEntityStatus?: 'empty' | 'loading' | 'error' | 'success'
  contactLocations?: EntityLocationDTO[]
  viewportContactLocations?: EntityLocationDTO[]
  searchAreaResult?: SearchAreaResponse
  selectedEntityVariant: MapPinProps['variant'] | null
  contactLocationsStatus?: 'empty' | 'loading' | 'error' | 'success'
  fallbackLocation?: BasicLocationDTO | null
  fallbackLocationStatus?: 'empty' | 'loading' | 'error' | 'success'
  searchTargetLocation?: { lat: number; lng: number } | null
  searchTargetAddress?: string | null
  searchFoundContactLocation?: EntityLocationDTO | null
  mapLinkClickedContactLocation?: EntityLocationDTO | null
  mapViewDialogOpen: boolean
  mapViewDialogCenter?: { lat: number; lng: number } | null
  mapViewDialogZoom?: number
  mapViewSource: 'sidenav' | 'addressLink' | null
  overrideFilters: boolean
  collisionDetectionEnabled: boolean
  searchAreaBoundary?: SearchAreaBoundary | null
  pestPacLocationDetail?: PestPacLocationDetailResponseDTO | null
  pestPacLocationDetailStatus?: 'empty' | 'loading' | 'error' | 'success'
  pestPacLocationPin?: EntityLocationDTO | null
  salespersonFilterOptions: SalespersonFilterOption[]
  salespersonFilterSelection: string[]
}

export const initialState: MapState = {
  entityDetails: null,
  selectedEntityStatus: 'empty',
  selectedServiceLocationId: undefined,
  selectedServiceLocations: [],
  selectedServiceLocationsDetails: [],
  contactLocations: [],
  viewportContactLocations: [],
  searchAreaResult: undefined,
  selectedEntityVariant: null,
  contactLocationsStatus: 'empty',
  fallbackLocation: null,
  fallbackLocationStatus: 'empty',
  searchTargetLocation: null,
  searchTargetAddress: null,
  searchFoundContactLocation: null,
  mapLinkClickedContactLocation: null,
  mapViewDialogOpen: false,
  mapViewDialogCenter: null,
  mapViewDialogZoom: undefined,
  mapViewSource: null,
  overrideFilters: false,
  collisionDetectionEnabled: true,
  searchAreaBoundary: null,
  pestPacLocationDetail: null,
  pestPacLocationDetailStatus: 'empty',
  pestPacLocationPin: null,
  salespersonFilterOptions: [],
  salespersonFilterSelection: [],
}

// SH-2100: whether a multi-record pin is really the matched record's own pin. Proximity alone isn't
// enough — a search runs with overrideFilters, so a neighbouring aggregate within the tolerance can
// hold hidden-by-filter members, and the match is preserved whole (filterLocationsByEntityTypes),
// which re-revealed those members over unrelated addresses and opened the drawer on the wrong pin.
// It is the match's own pin when it sits at the same coordinates, or lists the matched record.
function aggregateBelongsToMatch(
  aggregate: EntityLocationDTO,
  match: EntityLocationDTO,
): boolean {
  if (
    areColocatedCoordinates(
      aggregate.latitude,
      aggregate.longitude,
      match.latitude,
      match.longitude,
    )
  ) {
    return true
  }

  const members = aggregate.entities ?? []
  const matchedPestPacId = match.pestPacLocationId ?? null

  if (matchedPestPacId) {
    return members.some((m) => m.pestPacLocationId === matchedPestPacId)
  }

  if (match.entityId > 0) {
    const matchedType = match.entityType?.toLowerCase()
    return members.some(
      (m) =>
        m.entityId === match.entityId &&
        m.entityType?.toLowerCase() === matchedType,
    )
  }

  return false
}

// With overrideFilters=true the backend may omit aggregates, so the previous viewport's
// locations are searched too. Only the matched record's own aggregate is preferred.
function preferMultiTenantAggregate(
  match: EntityLocationDTO,
  sources: EntityLocationDTO[][],
): EntityLocationDTO {
  for (const source of sources) {
    const aggregate = source.find(
      (loc) =>
        loc.multitenant &&
        Math.abs(loc.latitude - match.latitude) < 0.0002 &&
        Math.abs(loc.longitude - match.longitude) < 0.0002 &&
        aggregateBelongsToMatch(loc, match),
    )
    if (aggregate) return aggregate
  }
  return match
}

// Runs the address/proximity match plus the multi-tenant-aggregate preference
// check, and applies the result to searchFoundContactLocation/searchTargetLocation.
// Shared by the immediate match (getContactLocationsInArea.fulfilled, when no
// PestPac data is pending) and the deferred match (resolveSearchMatch, once the
// PestPac-merged location set has arrived — see useApi.ts) so both paths resolve
// a search identically regardless of when the full data set becomes available.
function applySearchMatch(
  state: MapState,
  locations: EntityLocationDTO[],
  bbox?: { lat1: number; lat2: number; long1: number; long2: number },
) {
  if (!state.searchTargetLocation) return
  state.searchFoundContactLocation = null
  const { lat, lng } = state.searchTargetLocation
  const mtFallbackLocations = state.viewportContactLocations ?? []

  let bestMatch: EntityLocationDTO | null = null
  const matchResult = matchEntityByAddress(
    state.searchTargetAddress || '',
    locations,
    true,
    lat,
    lng,
  )
  if (matchResult) bestMatch = matchResult.entity

  // Prefer a multi-tenant aggregate pin over an individual entity pin.
  // With overrideFilters=true the backend omits aggregates, so also check
  // the previous contactLocations which were fetched with overrideFilters=false
  // and do include multi-tenant aggregate pins.
  if (bestMatch && !bestMatch.multitenant) {
    bestMatch = preferMultiTenantAggregate(bestMatch, [
      locations,
      mtFallbackLocations,
    ])
  }

  if (bestMatch) {
    state.searchFoundContactLocation = bestMatch
    state.searchTargetLocation = null
    state.searchTargetAddress = null
    return
  }

  if (!bbox) return

  // No match found. Only clear searchTargetLocation if the target was actually
  // inside the bbox of this request — if it was outside, the map hadn't
  // finished panning yet and we must leave it for the next call.
  const target = state.searchTargetLocation
  const { lat1, lat2, long1, long2 } = bbox
  const hasBbox = lat1 !== 0 || lat2 !== 0 || long1 !== 0 || long2 !== 0
  const inBbox =
    hasBbox &&
    target.lat <= lat1 &&
    target.lat >= lat2 &&
    target.lng >= long1 &&
    target.lng <= long2
  if (inBbox) {
    state.searchTargetLocation = null
    state.searchTargetAddress = null
  }
}

// SH-2100: shared by the immediate re-filter (getContactLocationsInArea.fulfilled, below) and
// the deferred one (resolveSearchMatch, once the PestPac-merged data lands) so overrideFilters'
// "backend returns everything" search strategy narrows back down to the real active filters
// identically regardless of when the full data set becomes available — mirrors applySearchMatch's
// own immediate/deferred split above. Order matters: entity-type, then knock-status name, then
// "Knock Status Locations Only" — each narrows what the previous one already let through.
export function applyClientSideRefilters(
  locations: EntityLocationDTO[],
  filters: {
    entityTypes: string
    filteredKnockStatusNames?: string[]
    knockStatusLocationsOnly?: boolean
  },
  preserveMatch: EntityLocationDTO | null | undefined,
): EntityLocationDTO[] {
  let result = filterLocationsByEntityTypes(
    locations,
    filters.entityTypes,
    preserveMatch,
  )

  if (filters.filteredKnockStatusNames?.length) {
    result = filterLocationsByKnockStatus(
      result,
      filters.filteredKnockStatusNames,
      preserveMatch,
    )
  }

  if (filters.knockStatusLocationsOnly) {
    result = filterEntityPinsForKnockStatusLocationsOnly(result, preserveMatch)
  }

  return result
}

const mapSlice = createSlice({
  name: 'Map',
  initialState,
  reducers: {
    setEntityDetails(state, action: PayloadAction<EntityDetail | null>) {
      state.entityDetails = action.payload
    },
    setEntityNextActionDate(state, action: PayloadAction<Date | null>) {
      if (state.entityDetails)
        state.entityDetails.nextActionDate = action.payload
    },
    clearEntityDetails(state) {
      state.entityDetails = null
    },
    setSelectedEntityStatus(
      state,
      action: PayloadAction<'empty' | 'loading' | 'error' | 'success'>,
    ) {
      state.selectedEntityStatus = action.payload
    },
    setSelectedServiceLocationsDetailsStatus(
      state,
      action: PayloadAction<'empty' | 'loading' | 'error' | 'success'>,
    ) {
      state.selectedServiceLocationsDetailsStatus = action.payload
    },
    setSelectedServiceLocationsDetails(
      state,
      action: PayloadAction<MultiTenantLocationDetails[]>,
    ) {
      state.selectedServiceLocationsDetails = action.payload
    },
    setSelectedServiceLocationId(
      state,
      action: PayloadAction<number | undefined>,
    ) {
      state.selectedServiceLocationId = action.payload
    },
    setSelectedServiceLocations(
      state,
      action: PayloadAction<MultiTenantLocation[]>,
    ) {
      state.selectedServiceLocations = action.payload
    },
    setSelectedEntityVariant(
      state,
      action: PayloadAction<MapPinProps['variant'] | null>,
    ) {
      state.selectedEntityVariant = action.payload
    },
    setFallbackLocation(state, action: PayloadAction<BasicLocationDTO | null>) {
      state.fallbackLocation = action.payload
    },
    setFallbackLocationStatus(
      state,
      action: PayloadAction<'empty' | 'loading' | 'error' | 'success'>,
    ) {
      state.fallbackLocationStatus = action.payload
    },
    clearFallbackLocation(state) {
      state.fallbackLocation = null
      state.fallbackLocationStatus = 'empty'
    },
    resetMapState() {
      return initialState
    },
    setSearchTargetLocation(
      state,
      action: PayloadAction<{
        lat: number
        lng: number
        address?: string
      } | null>,
    ) {
      // Capture before mutation — if already overriding, the cascade won't fire
      // because refreshMapPins deps won't change, so we must resolve immediately.
      const wasAlreadyOverriding = state.overrideFilters
      state.searchTargetLocation = action.payload
        ? { lat: action.payload.lat, lng: action.payload.lng }
        : null
      state.searchTargetAddress = action.payload?.address || null
      state.overrideFilters = !!action.payload

      // A new search target invalidates any match found for the previous one.
      // Without this, a search that resolves while a PestPac merge is still
      // pending (getContactLocationsInArea.fulfilled skips applySearchMatch in
      // that case) would use the stale match as `preserveMatch` and briefly
      // re-render the prior search's hidden-by-filter pin (SH-1341 regression).
      if (action.payload) {
        state.searchFoundContactLocation = null
      }

      // On first transition into search mode, snapshot viewport locations (which
      // may have been set by the tile cache path via setContactLocations rather than
      // getContactLocationsInArea) so the MT fallback has data to work with.
      if (action.payload && !wasAlreadyOverriding) {
        state.viewportContactLocations = state.contactLocations ?? []
      }

      if (
        action.payload &&
        wasAlreadyOverriding &&
        state.contactLocations?.length
      ) {
        const { lat, lng, address } = action.payload
        const matchResult = matchEntityByAddress(
          address || '',
          state.contactLocations,
          true,
          lat,
          lng,
        )
        if (matchResult) {
          let bestMatch: EntityLocationDTO = matchResult.entity
          if (!bestMatch.multitenant) {
            bestMatch = preferMultiTenantAggregate(bestMatch, [
              state.contactLocations ?? [],
              state.viewportContactLocations ?? [],
            ])
          }
          state.searchFoundContactLocation = bestMatch
          state.searchTargetLocation = null
          state.searchTargetAddress = null
        }
      }
    },
    setOverrideFilters(state, action: PayloadAction<boolean>) {
      state.overrideFilters = action.payload
    },
    setSearchFoundContactLocation(
      state,
      action: PayloadAction<EntityLocationDTO | null>,
    ) {
      state.searchFoundContactLocation = action.payload
    },
    setMapLinkClickedContactLocation(
      state,
      action: PayloadAction<EntityLocationDTO | null>,
    ) {
      state.mapLinkClickedContactLocation = action.payload
    },
    clearSelectedServiceLocation(state) {
      state.overrideFilters = false
      state.mapLinkClickedContactLocation = null
      state.searchFoundContactLocation = null
      state.contactLocations = []
      state.searchAreaResult = undefined
      state.selectedServiceLocations = []
      state.selectedServiceLocationId = undefined
    },
    setMapViewDialogOpen(
      state,
      action: PayloadAction<{
        open: boolean
        center?: { lat: number; lng: number } | H.geo.IPoint | null
        zoom?: number
        source?: 'sidenav' | 'addressLink' | null
      }>,
    ) {
      state.mapViewDialogOpen = action.payload.open
      if (
        action.payload.center !== undefined &&
        action.payload.center !== null
      ) {
        // Convert H.geo.Point to plain object if needed
        state.mapViewDialogCenter = {
          lat: action.payload.center.lat,
          lng: action.payload.center.lng,
        }
      }
      if (action.payload.zoom !== undefined) {
        state.mapViewDialogZoom = action.payload.zoom
      }
      if (action.payload.source !== undefined) {
        state.mapViewSource = action.payload.source
      }
      if (!action.payload.open) {
        // Reset when closing
        state.mapViewDialogCenter = null
        state.mapViewDialogZoom = undefined
        state.mapViewSource = null
      }
    },
    setCollisionDetectionEnabled(state, action: PayloadAction<boolean>) {
      state.collisionDetectionEnabled = action.payload
    },
    setContactLocations(state, action: PayloadAction<EntityLocationDTO[]>) {
      state.contactLocations = action.payload
      state.contactLocationsStatus = 'success'
    },
    // Resolves a search match that getContactLocationsInArea.fulfilled deferred
    // because a PestPac fetch was still pending (overrideFilters && pestPacActive).
    // Dispatched from useApi.ts once that fetch settles, with the same merged
    // location set that gets dispatched to setContactLocations.
    resolveSearchMatch(
      state,
      action: PayloadAction<{
        locations: EntityLocationDTO[]
        bbox: { lat1: number; lat2: number; long1: number; long2: number }
        entityTypes: string
        filteredKnockStatusNames?: string[]
        knockStatusLocationsOnly?: boolean
      }>,
    ) {
      applySearchMatch(state, action.payload.locations, action.payload.bbox)
      // SH-2100 bugfix: this path (PestPac-merged deferred match) used to rebuild
      // contactLocations from the raw location set without ever re-applying the
      // knock-status / "Locations Only" re-filters — so every pan/zoom while
      // pestPacActive is true (the common case at typical zoom levels) re-revealed
      // every hidden-by-filter pin in the viewport, reported as a leak/flash on
      // navigation. applyClientSideRefilters runs the same chain the immediate
      // (non-deferred) path below already does.
      state.contactLocations = applyClientSideRefilters(
        action.payload.locations,
        {
          entityTypes: action.payload.entityTypes,
          filteredKnockStatusNames: action.payload.filteredKnockStatusNames,
          knockStatusLocationsOnly: action.payload.knockStatusLocationsOnly,
        },
        state.searchFoundContactLocation,
      )
    },
    setSearchAreaResult(state, action: PayloadAction<SearchAreaResponse>) {
      state.searchAreaResult = action.payload
    },
    // Used by the tile-cache path to update all viewport counts from per-tile
    // backend responses. total* are derived from deduplicated in-viewport pins;
    // my* are summed from per-tile backend responses (tile bboxes don't overlap).
    updateSearchAreaResultTotals(
      state,
      action: PayloadAction<{
        total: number
        totalLead: number
        totalDraftLead: number
        totalOpportunity: number
        myRecords: number
        myLead: number
        myDraftLead: number
        myOpportunity: number
        locations: EntityLocationDTO[]
        displayedPinCount: number
      }>,
    ) {
      const existing = state.searchAreaResult
      state.searchAreaResult = {
        total: action.payload.total,
        myRecords: action.payload.myRecords,
        totalLead: action.payload.totalLead,
        totalDraftLead: action.payload.totalDraftLead,
        totalOpportunity: action.payload.totalOpportunity,
        myLead: action.payload.myLead,
        myOpportunity: action.payload.myOpportunity,
        myDraftLead: action.payload.myDraftLead,
        locations: action.payload.locations,
        filters: existing?.filters ?? '',
        displayedPinCount: action.payload.displayedPinCount,
      }
    },
    setContactLocationsStatus(
      state,
      action: PayloadAction<'empty' | 'loading' | 'error' | 'success'>,
    ) {
      state.contactLocationsStatus = action.payload
    },
    setSearchAreaBoundary(state, action: PayloadAction<SearchAreaBoundary>) {
      state.searchAreaBoundary = action.payload
    },
    clearSearchAreaBoundary(state) {
      state.searchAreaBoundary = null
    },
    setPestPacLocationDetail(
      state,
      action: PayloadAction<PestPacLocationDetailResponseDTO | null>,
    ) {
      state.pestPacLocationDetail = action.payload
    },
    setPestPacLocationDetailStatus(
      state,
      action: PayloadAction<'empty' | 'loading' | 'error' | 'success'>,
    ) {
      state.pestPacLocationDetailStatus = action.payload
    },
    setPestPacLocationPin(
      state,
      action: PayloadAction<EntityLocationDTO | null>,
    ) {
      state.pestPacLocationPin = action.payload
    },
    clearPestPacLocationDetail(state) {
      state.pestPacLocationDetail = null
      state.pestPacLocationDetailStatus = 'empty'
      state.pestPacLocationPin = null
    },
    setSalespersonFilterOptions(
      state,
      action: PayloadAction<SalespersonFilterOption[]>,
    ) {
      state.salespersonFilterOptions = action.payload
    },
    setSalespersonFilterSelection(state, action: PayloadAction<string[]>) {
      state.salespersonFilterSelection = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getContactLocationsInArea.fulfilled,
      (state, { payload, meta }) => {
        const { locations } = payload
        // Persist viewport-level (non-search) locations separately so they survive
        // subsequent search fetches (overrideFilters=true). These are the only source
        // for multi-tenant aggregate pins when a search response omits them.
        if (!meta.arg.overrideFilters) {
          state.viewportContactLocations = locations
        }
        state.contactLocations =
          meta.arg.overrideFilters && meta.arg.pestPacActive
            ? mergePestPacPinsForward(
                locations,
                state.contactLocations ?? [],
                // SH-2100: `locations` includes every entity type (overrideFilters), even
                // ones the active filter hides — passed here (rather than left to default
                // to `locations`) so a carried PestPac pin isn't wrongly suppressed by a
                // linked-but-about-to-be-filtered-out SC record. This is a "would it render
                // on its own filter merits" check (no preserveMatch), not the final rendered
                // set computed below — but it must still include knockStatusLocationsOnly:
                // meta.arg.entityTypes is widened to every record type whenever that mode is
                // active (so the backend still returns them for search-match preservation),
                // so entityTypes alone no longer excludes a linked non-KS record here (Copilot
                // review finding — the omission left the same class of bug this fix targets).
                applyClientSideRefilters(
                  locations,
                  {
                    entityTypes: meta.arg.entityTypes,
                    filteredKnockStatusNames: payload.filteredKnockStatusNames,
                    knockStatusLocationsOnly: meta.arg.knockStatusLocationsOnly,
                  },
                  null,
                ),
              )
            : locations
        state.searchAreaResult = payload
        state.contactLocationsStatus = 'success'

        // When overrideFilters + pestPacActive, useApi.ts fetches PestPac data
        // right after this and dispatches resolveSearchMatch with the merged set.
        // `locations` here is Sales-Center-only and never contains a searched
        // PestPac record, so matching against it now would either miss the
        // target entirely or fall back to proximity-matching an unrelated pin
        // (surfaced by manual validation of the carry-forward fix — SH-1341).
        const pestPacMergeIsPending =
          meta.arg.overrideFilters && meta.arg.pestPacActive
        if (!pestPacMergeIsPending) {
          applySearchMatch(
            state,
            locations,
            meta.arg.overrideFilters
              ? {
                  lat1: meta.arg.lat1,
                  lat2: meta.arg.lat2,
                  long1: meta.arg.long1,
                  long2: meta.arg.long2,
                }
              : undefined,
          )
        }

        // overrideFilters makes the backend return every Sales Center entity type and
        // every knock status (so a hidden-type/hidden-KS/hidden-by-"Locations Only" record
        // can still be found by address search), which also reveals every OTHER
        // hidden-by-filter record. applyClientSideRefilters re-applies all three filter
        // dimensions to what actually renders, keeping the searched-for match visible even
        // if it would otherwise be filtered out (SH-1341/SH-2100). This must run even while
        // the PestPac merge is pending — deferring it until resolveSearchMatch left a real,
        // visible window (the length of the PestPac fetch) where hidden-by-filter pins
        // rendered unfiltered before vanishing, reintroducing the class of blink this branch
        // was built to eliminate. PestPac pins are never filtered here, so the carry-forward
        // above is unaffected. payload.filteredKnockStatusNames is only attached by the
        // thunk when overrideFilters is true AND a real KS filter is active.
        if (meta.arg.overrideFilters) {
          state.contactLocations = applyClientSideRefilters(
            state.contactLocations ?? [],
            {
              entityTypes: meta.arg.entityTypes,
              filteredKnockStatusNames: payload.filteredKnockStatusNames,
              knockStatusLocationsOnly: meta.arg.knockStatusLocationsOnly,
            },
            state.searchFoundContactLocation,
          )
        }
      },
    )
    builder.addCase(getContactLocationsInArea.pending, (state) => {
      state.contactLocationsStatus = 'loading'
    })

    builder.addCase(getContactLocationsInArea.rejected, (state, action) => {
      const statusCode = action.payload?.StatusCode
      if (statusCode === 304) {
        state.contactLocationsStatus = 'success'
      } else if (statusCode !== 0) {
        // StatusCode 0 = aborted (new viewport request already in-flight) — leave as 'loading'
        state.contactLocationsStatus = 'error'
      }
    })

    builder.addCase(getEntityDetail.fulfilled, (state, action) => {
      state.entityDetails = action.payload
      state.selectedEntityStatus = 'success'
    })

    builder.addCase(getEntityDetail.rejected, (state) => {
      state.selectedEntityStatus = 'error'
      state.entityDetails = null
    })

    builder.addCase(getEntityProducts.fulfilled, (state, action) => {
      if (!state.entityDetails || !('services' in state.entityDetails)) return
      state.entityDetails.services = action.payload
    })

    builder.addCase(getEntityProducts.rejected, (state) => {
      if (!state.entityDetails || !('services' in state.entityDetails)) return
      state.entityDetails.services = null
    })

    builder.addCase(fetchMultiTenantEntityDetail.fulfilled, (state, action) => {
      state.selectedServiceLocationsDetailsStatus = 'success'
      state.selectedServiceLocationsDetails = action.payload
        .map((item: LocationDTO) => {
          const matched = state.selectedServiceLocations?.find(
            (loc) => loc.contactLocationId === item.contactLocationId,
          )
          if (matched) {
            return {
              ...matched,
              ...item,
            } as MultiTenantLocationDetails
          }
          return undefined
        })
        .filter(
          (item): item is MultiTenantLocationDetails => item !== undefined,
        )
    })

    builder.addCase(fetchMultiTenantEntityDetail.rejected, (state) => {
      state.selectedServiceLocationsDetails = []
      state.selectedServiceLocationsDetailsStatus = 'error'
    })
  },
})

export const {
  setEntityDetails,
  clearEntityDetails,
  setSelectedEntityStatus,
  setSelectedServiceLocationId,
  setSelectedServiceLocations,
  setSelectedEntityVariant,
  setFallbackLocation,
  setFallbackLocationStatus,
  clearFallbackLocation,
  setSelectedServiceLocationsDetailsStatus,
  setSelectedServiceLocationsDetails,
  resetMapState,
  setSearchTargetLocation,
  setSearchFoundContactLocation,
  setMapLinkClickedContactLocation,
  setMapViewDialogOpen,
  setOverrideFilters,
  clearSelectedServiceLocation,
  setEntityNextActionDate,
  setCollisionDetectionEnabled,
  setContactLocations,
  resolveSearchMatch,
  setSearchAreaResult,
  updateSearchAreaResultTotals,
  setContactLocationsStatus,
  setSearchAreaBoundary,
  clearSearchAreaBoundary,
  setPestPacLocationDetail,
  setPestPacLocationDetailStatus,
  setPestPacLocationPin,
  clearPestPacLocationDetail,
  setSalespersonFilterOptions,
  setSalespersonFilterSelection,
} = mapSlice.actions

export default mapSlice.reducer

export const selectMapEntityDetails = (state: StoreData) =>
  state.map.entityDetails

export const selectEntityStatus = (state: StoreData) =>
  state.map.selectedEntityStatus

export const selectServiceLocationId = (state: StoreData) =>
  state.map.selectedServiceLocationId

export const selectLocation = (state: StoreData) => {
  const id = state.map.selectedServiceLocationId
  const entityType = state.map.selectedEntityVariant

  if (entityType === 'pendinglead' || entityType === 'draftlead') {
    const draftLead = state.map.entityDetails as unknown as DraftLead
    if (!draftLead) return undefined

    // Create a LocationDTO from the DraftLead
    return {
      id: draftLead.id,
      billingLocation: false,
      contactId: -1, // Not available on DraftLead
      salesTeamId: draftLead.salesTeamId ? Number(draftLead.salesTeamId) : -1,
      branchId: draftLead.branchId ? Number(draftLead.branchId) : null,
      branch: draftLead.branch,
      locationType: draftLead.locationType,
      locationTypeId: draftLead.locationTypeId,
      divisionId: draftLead.divisionId,
      division: draftLead.division,
      firstName: draftLead.firstName,
      lastName: draftLead.lastName,
      title: draftLead.title,
      company: draftLead.company,
      emailAddresses: draftLead.emailAddress
        ? [{ emailAddress: draftLead.emailAddress, isPrimary: true }]
        : [],
      phoneNumber: draftLead.phoneNumber,
      phoneExt: draftLead.phoneExt,
      altPhoneNumber: draftLead.altPhoneNumber,
      altPhoneExt: draftLead.altPhoneExt,
      street1: draftLead.street1,
      street2: draftLead.street2,
      city: draftLead.city,
      stateAbbreviation: draftLead.stateAbbreviation,
      postalCode: draftLead.postalCode,
      county: null, // Not available on DraftLead
      country: draftLead.country,
      longitude: draftLead.longitude ?? null,
      latitude: draftLead.latitude ?? null,
      externalLinkId: draftLead.locationId ?? null,
      locationCode: draftLead.locationCode ?? null,
      knockStatusDetails: draftLead.knockStatusDetails,
    } as LocationDTO
  } else if (
    state.map.entityDetails &&
    'locations' in state.map.entityDetails &&
    state.map.entityDetails.locations
  ) {
    const locations = state.map.entityDetails.locations.serviceLocations || []
    return locations.find((loc) => loc.id === id)
  }
}

export const selectServiceLocations = (state: StoreData) =>
  state.map.selectedServiceLocations || []

export const selectServiceLocationsDetails = (state: StoreData) => ({
  locations: state.map.selectedServiceLocationsDetails || [],
  status: state.map.selectedServiceLocationsDetailsStatus || 'empty',
})

export const selectMapEntityVariant = (state: StoreData) =>
  state.map.selectedEntityVariant || null

export const selectContactLocations = (state: StoreData) =>
  state.map.contactLocations || []
export const selectContactLocationsStatus = (state: StoreData) =>
  state.map.contactLocationsStatus || 'empty'

export const selectFallbackLocation = (state: StoreData) =>
  state.map.fallbackLocation || null

export const selectFallbackLocationStatus = (state: StoreData) =>
  state.map.fallbackLocationStatus || 'empty'

export const selectSearchAreaResult = (state: StoreData) =>
  state.map.searchAreaResult

export const selectSearchTargetAddress = (state: StoreData) =>
  state.map.searchTargetAddress

export const selectSearchFoundContactLocation = (state: StoreData) =>
  state.map.searchFoundContactLocation || null

export const selectMapLinkClickedContactLocation = (state: StoreData) =>
  state.map.mapLinkClickedContactLocation || null

export const selectMapViewDialogOpen = (state: StoreData) =>
  state.map.mapViewDialogOpen

export const selectMapViewDialogCenter = (state: StoreData) =>
  state.map.mapViewDialogCenter

export const selectMapViewDialogZoom = (state: StoreData) =>
  state.map.mapViewDialogZoom

export const selectMapViewSource = (state: StoreData) => state.map.mapViewSource

export const selectOverrideFilters = (state: StoreData) =>
  state.map.overrideFilters

export const selectCollisionDetectionEnabled = (state: StoreData) =>
  state.map.collisionDetectionEnabled

export const selectSearchAreaBoundary = (state: StoreData) =>
  state.map.searchAreaBoundary || null

export const selectSalespersonFilterOptions = (state: StoreData) =>
  state.map.salespersonFilterOptions || []

export const selectSalespersonFilterSelection = (state: StoreData) =>
  state.map.salespersonFilterSelection || []

export const selectEntityTypeDisplay = (state: StoreData) => {
  const variant = state.map.selectedEntityVariant
  switch (variant?.toLowerCase()) {
    case 'lead':
      return 'Lead'
    case 'opportunity':
      return 'Opportunity'
    case 'pendinglead':
    case 'draftlead':
      return 'Pending Lead'
    default:
      return 'Unknown'
  }
}

export const selectPestPacLocationDetail = (state: StoreData) =>
  state.map.pestPacLocationDetail || null

export const selectPestPacLocationDetailStatus = (state: StoreData) =>
  state.map.pestPacLocationDetailStatus || 'empty'

export const selectPestPacLocationPin = (state: StoreData) =>
  state.map.pestPacLocationPin || null
